/**
 * CaseSetupMessagingContext
 *
 * Shared state for case setup intro messaging within the ingestion wizard.
 * Lives at the Review step level so both the messaging preview panel
 * and the FinishButton can access the same draft state.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useGetFirmQuery } from '@/services/api/firmsService';
import {
  useCaseSetupSendIntroEmailMutation,
  useCaseSetupSendIntroSmsMutation,
} from '@/services/api/generated/caseSetupService';
import {
  useGetMatterDetailsQuery,
  usePatchMatterClientMutation,
} from '@/services/api/mattersService';
import {
  PreferredLanguage,
  type PreferredLanguageValue,
} from '@/services/types/matter-client-types';

interface CaseSetupMessagingState {
  // SMS
  smsEnabled: boolean;
  setSmsEnabled: (v: boolean) => void;
  smsMessage: string;
  setSmsMessage: (v: string) => void;
  smsIsLoading: boolean;
  setSmsIsLoading: (v: boolean) => void;
  smsUnavailableReason: string | undefined;

  // Email
  emailEnabled: boolean;
  setEmailEnabled: (v: boolean) => void;
  emailSubject: string;
  setEmailSubject: (v: string) => void;
  emailBody: string;
  setEmailBody: (v: string) => void;
  emailIsLoading: boolean;
  setEmailIsLoading: (v: boolean) => void;
  emailUnavailableReason: string | undefined;

  // Language
  language: PreferredLanguageValue;
  setLanguage: (lang: PreferredLanguageValue) => void;
  isLanguageUpdating: boolean;

  // Derived
  willSendSms: boolean;
  willSendEmail: boolean;
  willSendAnything: boolean;
  draftsLoading: boolean;

  // Actions
  sendMessages: () => Promise<{ successes: number; failures: number }>;
  isSending: boolean;
}

/**
 * Exported so components that may or may not be inside the provider
 * (e.g. FinishButton) can use `useContext(CaseSetupMessagingContext)` safely —
 * it returns `null` when outside the provider tree.
 */
export const CaseSetupMessagingContext = createContext<CaseSetupMessagingState | null>(null);

export const useCaseSetupMessaging = () => {
  const ctx = useContext(CaseSetupMessagingContext);
  if (!ctx) {
    throw new Error('useCaseSetupMessaging must be used within CaseSetupMessagingProvider');
  }
  return ctx;
};

interface ProviderProps {
  matterId: string;
  children: React.ReactNode;
}

export const CaseSetupMessagingProvider: React.FC<ProviderProps> = ({
  matterId,
  children,
}) => {
  // SMS state
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsMessage, setSmsMessage] = useState('');
  const [smsIsLoading, setSmsIsLoading] = useState(false);

  // Email state
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailIsLoading, setEmailIsLoading] = useState(false);

  // Sending state
  const [isSending, setIsSending] = useState(false);

  // Fetch matter + firm for availability checks
  const { data: matter } = useGetMatterDetailsQuery(matterId);
  const { data: firmData } = useGetFirmQuery(matter?.firm?.id ?? '', {
    skip: !matter?.firm?.id,
  });

  // Language state — default from client's preferred_language
  const primaryClient = matter?.clients?.[0];
  const [language, setLanguageState] = useState<PreferredLanguageValue>(
    (primaryClient?.preferred_language as PreferredLanguageValue) ?? PreferredLanguage.ENGLISH,
  );
  const [patchMatterClient, { isLoading: isLanguageUpdating }] = usePatchMatterClientMutation();

  // Sync language state when matter data loads
  useEffect(() => {
    if (primaryClient?.preferred_language) {
      setLanguageState(primaryClient.preferred_language as PreferredLanguageValue);
    }
  }, [primaryClient?.preferred_language]);

  const setLanguage = useCallback(
    (lang: PreferredLanguageValue) => {
      const previousLanguage = language;
      setLanguageState(lang);

      // Persist to Client.preferred_language
      if (primaryClient?.id) {
        patchMatterClient({
          matter_id: matterId,
          matter_client_id: primaryClient.id,
          patch: { preferred_language: lang },
        })
          .unwrap()
          .catch(() => {
            setLanguageState(previousLanguage);
          });
      }
    },
    [matterId, primaryClient?.id, patchMatterClient, language],
  );

  const canContactClient = matter?.contact_client_directly !== false;

  const hasClientPhone = useMemo(
    () =>
      matter?.clients?.some(
        (c) => c.phone_numbers && c.phone_numbers.length > 0,
      ) ?? false,
    [matter?.clients],
  );

  const hasClientEmail = useMemo(
    () => matter?.clients?.some((c) => !!c.email) ?? false,
    [matter?.clients],
  );

  const smsSenderConfigured = Boolean(firmData?.sms_sender_number);
  const hasFirmFrontChannel = Boolean(firmData?.front_channel?.front_id);

  const smsUnavailableReason = useMemo(() => {
    if (!canContactClient) return 'Client contact is disabled for this matter.';
    if (!hasClientPhone) return 'No phone number on file for this client.';
    if (!smsSenderConfigured) return 'SMS sender number is not configured for this firm.';
    return undefined;
  }, [canContactClient, hasClientPhone, smsSenderConfigured]);

  const emailUnavailableReason = useMemo(() => {
    if (!canContactClient) return 'Client contact is disabled for this matter.';
    if (!hasClientEmail) return 'No email address on file for this client.';
    if (!hasFirmFrontChannel) return 'No Front email channel is configured for this firm.';
    return undefined;
  }, [canContactClient, hasClientEmail, hasFirmFrontChannel]);

  // Auto-disable when messaging becomes unavailable (e.g. phone removed, contact disabled)
  useEffect(() => {
    if (smsUnavailableReason) setSmsEnabled(false);
  }, [smsUnavailableReason]);
  useEffect(() => {
    if (emailUnavailableReason) setEmailEnabled(false);
  }, [emailUnavailableReason]);

  // Derived
  const willSendSms = smsEnabled && !!smsMessage.trim();
  const willSendEmail = emailEnabled && !!emailBody.trim();
  const willSendAnything = willSendSms || willSendEmail;
  const draftsLoading = smsIsLoading || emailIsLoading;

  // Send mutations
  const [sendIntroSms] = useCaseSetupSendIntroSmsMutation();
  const [sendIntroEmail] = useCaseSetupSendIntroEmailMutation();

  const sendMessages = useCallback(async () => {
    setIsSending(true);
    try {
      const promises: Promise<unknown>[] = [];

      if (willSendSms) {
        promises.push(
          sendIntroSms({
            matterId,
            sendCaseSetupSmsRequest: { matter_id: matterId, message: smsMessage },
          }).unwrap(),
        );
      }

      if (willSendEmail) {
        promises.push(
          sendIntroEmail({
            matterId,
            sendCaseSetupEmailRequest: {
              matter_id: matterId,
              subject: emailSubject,
              body: emailBody,
            },
          }).unwrap(),
        );
      }

      const results = await Promise.allSettled(promises);
      const failures = results.filter((r) => r.status === 'rejected').length;
      const successes = results.filter((r) => r.status === 'fulfilled').length;

      return { successes, failures };
    } finally {
      setIsSending(false);
    }
  }, [willSendSms, willSendEmail, smsMessage, emailSubject, emailBody, matterId, sendIntroSms, sendIntroEmail]);

  const value = useMemo<CaseSetupMessagingState>(
    () => ({
      smsEnabled,
      setSmsEnabled,
      smsMessage,
      setSmsMessage,
      smsIsLoading,
      setSmsIsLoading,
      smsUnavailableReason,
      emailEnabled,
      setEmailEnabled,
      emailSubject,
      setEmailSubject,
      emailBody,
      setEmailBody,
      emailIsLoading,
      setEmailIsLoading,
      emailUnavailableReason,
      language,
      setLanguage,
      isLanguageUpdating,
      willSendSms,
      willSendEmail,
      willSendAnything,
      draftsLoading,
      sendMessages,
      isSending,
    }),
    [
      smsEnabled,
      smsMessage,
      smsIsLoading,
      smsUnavailableReason,
      emailEnabled,
      emailSubject,
      emailBody,
      emailIsLoading,
      emailUnavailableReason,
      language,
      setLanguage,
      isLanguageUpdating,
      willSendSms,
      willSendEmail,
      willSendAnything,
      draftsLoading,
      sendMessages,
      isSending,
    ],
  );

  return (
    <CaseSetupMessagingContext.Provider value={value}>
      {children}
    </CaseSetupMessagingContext.Provider>
  );
};
